/**
 * Onglet Crédit — liste des crédits en cours + scoring client.
 *
 * Lit en LOCAL depuis Dexie (les crédits créés via les ventes à crédit).
 * Affichage par urgence (échéance la plus proche d'abord).
 */
import { useEffect, useState } from 'react';
import { Credit } from '@boutikos/domain';
import { db, type CreditRow, type ClientRow } from '../lib/db';
import { argent, dateCourte, joursAvant } from '../lib/format';

export function CreditTab() {
  const [credits, setCredits] = useState<CreditRow[]>([]);
  const [clients, setClients] = useState<Map<string, ClientRow>>(new Map());

  useEffect(() => {
    void (async () => {
      const all = await db.credits.where('statut').notEqual('paye').toArray();
      all.sort((a, b) => a.echeance_at - b.echeance_at);
      setCredits(all);
      const cls = await db.clients.toArray();
      setClients(new Map(cls.map((c) => [c.id, c])));
    })();
  }, []);

  const balance = Credit.calculerBalanceAgee(
    credits.map((c) => ({
      id: c.id,
      clientId: c.client_id,
      boutiqueId: '',
      venteId: c.vente_id,
      montant: c.montant,
      paye: c.paye,
      echeanceAt: c.echeance_at,
      statut: c.statut as 'ouvert' | 'relance' | 'bloque' | 'paye',
      scoringAuMoment: c.scoring_au_moment,
      createdAt: c.updated_at,
    })),
    Date.now(),
  );

  const marquerPaye = async (id: string) => {
    await db.credits.update(id, { statut: 'paye', paye: 0, updated_at: Date.now() });
    setCredits((cs) => cs.filter((c) => c.id !== id));
  };

  return (
    <>
      <h1 className="titre-section">💳 Crédit</h1>

      <section className="carte">
        <h3>Balance âgée</h3>
        <div className="ligne"><span>0-30j</span><strong>{argent(balance.tranches['0-30'].montant)}</strong></div>
        <div className="ligne"><span>31-60j</span><strong>{argent(balance.tranches['31-60'].montant)}</strong></div>
        <div className="ligne"><span>61-90j</span><strong>{argent(balance.tranches['61-90'].montant)}</strong></div>
        <div className="ligne"><span>90j+</span><strong style={{ color: 'var(--couleur-erreur)' }}>{argent(balance.tranches['90+'].montant)}</strong></div>
        <div className="ligne" style={{ fontWeight: 700, color: 'var(--couleur-primaire)' }}>
          <span>Total dû</span><span>{argent(balance.total)}</span>
        </div>
      </section>

      <section className="carte">
        <h3>Crédits en cours ({credits.length})</h3>
        {credits.length === 0 ? (
          <p className="etat-vide">Aucun crédit en cours. Les ventes à crédit apparaîtront ici.</p>
        ) : (
          credits.map((c) => {
            const cli = clients.get(c.client_id);
            const jr = joursAvant(c.echeance_at);
            const reste = c.montant - c.paye;
            const badge = jr < 0 ? 'bloque' : jr <= 3 ? 'attente' : 'ouvert';
            const label = jr < 0 ? `${-jr}j retard` : jr === 0 ? "aujourd'hui" : `dans ${jr}j`;
            return (
              <div key={c.id} className="ligne" style={{ alignItems: 'flex-start', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                  <strong>{cli?.nom ?? 'Client inconnu'}</strong>
                  <span className={`badge ${badge}`}>{label}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--couleur-texte-doux)' }}>
                  {argent(c.paye)} / {argent(c.montant)} — échéance {dateCourte(c.echeance_at)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                  <span>Reste : <strong>{argent(reste)}</strong></span>
                  <button className="bouton bouton-primaire" style={{ padding: '6px 12px', fontSize: 13 }} onClick={() => marquerPaye(c.id)}>
                    ✅ Marquer payé
                  </button>
                </div>
              </div>
            );
          })
        )}
      </section>
    </>
  );
}
